import { NextPageWithLayout } from "@bptypes/layout";
import { TitleControls } from "@components/headings";
import { ReleasesList } from "@components/lists";
import { ReleasesTable } from "@components/tables/ReleasesTable";
import MainLayout from "@layouts/MainLayout";
import { DEFAULT_LOCALE } from "@lib/constants";
import { exportAnonSession, useSessionContext } from "@lib/context/session";
import { useMediaQuery } from "@lib/hooks/useMediaQuery";
import { getTopReleasesQuery } from "@lib/network/releases";
import { getLocationData } from "@lib/utils/getLocationData";
import { device } from "@styles/theme";
import { dehydrate, DehydratedState, QueryClient, useQuery } from "@tanstack/react-query";
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { ReactElement } from "react";

const TOTAL_RELEASES = 100;
const TITLE = "Top 100 Releases";

interface Props {
	anonSession: AnonSession;
}

const Top100Releases: NextPageWithLayout<Props> = ({ anonSession }) => {
	const { getAccessToken, importAnonSession } = useSessionContext();
	importAnonSession(anonSession);
	const accessToken = getAccessToken();
	const isDesktop = useMediaQuery({ query: device.xl });
	const releasesQuery = useQuery(
		getTopReleasesQuery({
			params: { per_page: TOTAL_RELEASES },
			number: TOTAL_RELEASES,
			accessToken,
		}),
	);

	return (
		<>
			<TitleControls title={TITLE} />
			{isDesktop ?
					(
						<ReleasesTable
							location={TITLE}
							showNumbers={true}
							isLoading={releasesQuery.isLoading}
							releases={releasesQuery.data ? releasesQuery.data.results : []}
						/>
					) :
					(
						<ReleasesList
							location={TITLE}
							showNumbers
							showArtwork
							releases={releasesQuery.data ? releasesQuery.data.results : []}
						/>
					)}
		</>
	);
};

export default Top100Releases;

const PageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { t } = useTranslation("translation");

	return (
		<MainLayout
			title={`${t("Actions.View")} Beatport ${t("Top.Top100Releases")}`}
			metaDescription={t("Top.CheckOutTop100")}
		>
			{children}
		</MainLayout>
	);
};

Top100Releases.getLayout = (page: ReactElement) => (
	<PageLayout>
		{page}
	</PageLayout>
);

type ServerSideProps = {
	dehydratedState: DehydratedState;
	anonSession: AnonSession;
};

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
	ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<ServerSideProps>> => {
	const queryClient = new QueryClient();

	const location = getLocationData(ctx.req);

	await queryClient
		.fetchQuery(
			getTopReleasesQuery({
				params: { per_page: TOTAL_RELEASES },
				number: TOTAL_RELEASES,
				location,
			}),
		)
		.then((res) => res)
		.catch(() => { });

	const anonSession = await exportAnonSession();

	return {
		props: {
			dehydratedState: dehydrate(queryClient),
			anonSession,
			...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, ["translation"])),
		},
	};
};
